import { DashboardCardConfig } from './dashboard-page.types';
import PhotoCameraFrontOutlinedIcon from '@mui/icons-material/PhotoCameraFrontOutlined';
import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ShoppingList from '../shopping-list';
import OpenVisits from '../../features/visits/components/visits-list/visits-list-open';
import RevenueChart from '../../features/finance/revenue-report/RevenueChart';
import Visits from '../visits-page';
import useIntl from 'react-intl/src/components/useIntl';
import {
  ONBOARDING_STEP_KEYS,
  useOnboardingGuideVisibility,
} from '@features/onboarding/guide-visibility';
import { IconButton } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { usePendingReservationsQuery } from '@features/pending-reservations/queries';
import PendingReservationsCard from './PendingReservationsCard';
import OnboardingGuide from './onboarding-guide';

export const useDashboardPage = () => {
  const intl = useIntl();
  const { isVisible: isOnboardingGuideVisible, dismissGuide } =
    useOnboardingGuideVisibility(ONBOARDING_STEP_KEYS);

  const { data: pendingReservationsData } = usePendingReservationsQuery();
  const pendingReservations =
    pendingReservationsData?.filter(reservation => reservation.status === 'PENDING') ?? [];

  const DASHBOARD_CARDS_CONFIG: DashboardCardConfig[] = [
    ...(pendingReservations.length > 0
      ? [
          {
            id: 'pendingReservations',
            title: intl.formatMessage({
              id: 'dashboard.pendingReservations.title',
              defaultMessage: 'Nové žádosti o rezervaci',
            }),
            icon: <NotificationsActiveRoundedIcon fontSize="medium" color="secondary" />,
            component: <PendingReservationsCard reservations={pendingReservations} />,
            sx: { height: 'auto' },
          },
        ]
      : []),
    ...(isOnboardingGuideVisible
      ? [
          {
            id: 'onboardingGuide',
            title: intl.formatMessage({
              id: 'dashboard.onboarding.title',
            }),
            icon: <ChecklistRoundedIcon fontSize="medium" color="secondary" />,
            action: (
              <IconButton
                size="small"
                onClick={dismissGuide}
                aria-label={intl.formatMessage({
                  id: 'dashboard.onboarding.dismiss',
                  defaultMessage: 'Zavřít průvodce',
                })}
                sx={{ ml: 'auto' }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            ),
            component: <OnboardingGuide />,
            sx: { height: 'auto' },
          },
        ]
      : []),
    {
      id: 'visitsOverview',
      title: intl.formatMessage({
        id: 'dashboard.visitsOverview',
        defaultMessage: 'Přehled návštěv',
      }),
      icon: <PhotoCameraFrontOutlinedIcon fontSize="medium" color="secondary" />,
      component: <Visits type="dashBoardVisitOverView" />,
    },
    {
      id: 'shoppingList',
      title: intl.formatMessage({
        id: 'dashboard.shoppingList',
        defaultMessage: 'Nákupní seznam',
      }),
      icon: <AddShoppingCartOutlinedIcon fontSize="medium" color="secondary" />,
      component: <ShoppingList columnHeaderHeight={0} hideFooter={true} />,
    },
    {
      id: 'unclosedVisits',
      title: intl.formatMessage({
        id: 'dashboard.unclosedVisits',
        defaultMessage: 'Neuzavřené návštěvy',
      }),
      icon: <NewReleasesIcon fontSize="medium" color="secondary" />,
      component: <OpenVisits />,
    },
    {
      id: 'revenue',
      title: intl.formatMessage({
        id: 'dashboard.revenue',
        defaultMessage: 'Tržby',
      }),
      icon: <BarChartOutlinedIcon fontSize="medium" color="secondary" />,
      component: <RevenueChart />,
      sx: { height: 'auto' },
    },
  ];

  return { config: DASHBOARD_CARDS_CONFIG };
};
